import { useSyncExternalStore } from "react";
import { useRouterState } from "@tanstack/react-router";
import { hasQuizAnswers } from "@wags/matchmaker/pure.ts";
import {
    safeSessionGet,
    safeSessionRemove,
    safeSessionSet,
} from "@wags/tanstack-start/browser";
import {
    locatedMatchSearchSchema,
    matchSearchSchema,
    rememberedMatchSearchSchema,
    type MatchSearch,
    type RememberedMatchSearch,
} from "./last-match-search.contract";

const STORAGE_KEY = "wags:last-match-search";

const VOLATILE_KEYS = ["page", "limit", "pickerChosen", "demo"];

const listeners = new Set<() => void>();

function isReplayableMatchSearch(search: object): search is MatchSearch {
    if (!matchSearchSchema.safeParse(search).success) return false;
    return (
        hasQuizAnswers(search) ||
        locatedMatchSearchSchema.safeParse(search).success
    );
}

function snapshotSearch(search: object | undefined): MatchSearch | undefined {
    if (!search || !isReplayableMatchSearch(search)) return undefined;
    const kept = { ...search };
    for (const key of Object.keys(kept)) {
        if (VOLATILE_KEYS.includes(key) || key.startsWith("utm_")) {
            delete kept[key];
        }
    }
    return kept;
}

const ANON_OWNER = "anon";

function parseStoredSearch(raw: string) {
    try {
        return rememberedMatchSearchSchema.safeParse(JSON.parse(raw)).data;
    } catch {
        return undefined;
    }
}

function readSession(): RememberedMatchSearch | undefined {
    const raw = safeSessionGet(STORAGE_KEY);
    const stored = raw ? parseStoredSearch(raw) : undefined;
    const kept = snapshotSearch(stored?.search);
    return stored && kept ? { owner: stored.owner, search: kept } : undefined;
}

let remembered = readSession();
let viewer: string | null | undefined;
let snapshot = visibleSearch();

function visibleSearch() {
    if (!remembered) return undefined;
    const owned =
        remembered.owner === ANON_OWNER || remembered.owner === viewer;
    return owned ? remembered.search : undefined;
}

function emit() {
    for (const listener of listeners) listener();
}

function publish() {
    const next = visibleSearch();
    if (next === snapshot) return;
    snapshot = next;
    emit();
}

function store(next: RememberedMatchSearch | undefined) {
    remembered = next;
    if (next) {
        safeSessionSet(STORAGE_KEY, JSON.stringify(next));
    } else {
        safeSessionRemove(STORAGE_KEY);
    }
    publish();
}

function subscribe(callback: () => void) {
    listeners.add(callback);
    return () => {
        listeners.delete(callback);
    };
}

function ownerOf(userId: string | null) {
    return userId ?? ANON_OWNER;
}

export function setMatchSearchViewer(userId: string | null | undefined) {
    viewer = userId;
    if (userId === undefined || !remembered) {
        publish();
        return;
    }
    const owner = ownerOf(userId);
    if (remembered.owner === owner) {
        publish();
    } else if (remembered.owner === ANON_OWNER) {
        store({ owner, search: remembered.search });
    } else {
        store(undefined);
    }
}

export function rememberMatchSearch(search: MatchSearch | undefined) {
    if (typeof window === "undefined") return;
    const next = snapshotSearch(search);
    if (!next) return;
    const owner =
        viewer === undefined
            ? (remembered?.owner ?? ANON_OWNER)
            : ownerOf(viewer);
    if (
        remembered?.owner === owner &&
        JSON.stringify(remembered.search) === JSON.stringify(next)
    ) {
        return;
    }
    store({ owner, search: next });
}

type MatchLocation = { pathname: string; search: object };

export function liveMatchSearch({
    pathname,
    search,
}: MatchLocation): MatchSearch | undefined {
    const onMatchSurface =
        pathname === "/match" ||
        (pathname === "/filter-quiz" && hasQuizAnswers(search));
    return onMatchSurface && isReplayableMatchSearch(search)
        ? search
        : undefined;
}

export function rememberMatchSearchFromLocation(location: MatchLocation) {
    rememberMatchSearch(liveMatchSearch(location));
}

export function useLastMatchSearch() {
    return useSyncExternalStore(
        subscribe,
        () => snapshot,
        () => undefined,
    );
}

export function useChromeMatchSearch() {
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const search = useRouterState({
        select: (state) => state.location.search,
    });
    const stored = useLastMatchSearch();
    return liveMatchSearch({ pathname, search }) ?? stored;
}
